// Client-side invoice filter model + predicate (used by the FilterBar component
// and every queue screen). Kept out of the component file so the component
// module exports only a component (react-refresh).
import type { Invoice } from './types'
import type { InvoiceListParams } from '../api/types'
import { toNumber } from './format'

export interface InvoiceFilter {
  /** Display name of the picked company, and its id. The API filters on the
      id — authority and visibility key on the id everywhere, and a company may
      be renamed — while the name is what the popover shows. */
  company: string
  companyId: string
  vendor: string
  vendorId: string
  minTotal: string
  maxTotal: string
  fromDate: string
  toDate: string
}

export const emptyFilter: InvoiceFilter = {
  company: '',
  companyId: '',
  vendor: '',
  vendorId: '',
  minTotal: '',
  maxTotal: '',
  fromDate: '',
  toDate: '',
}

export function isFilterActive(f: InvoiceFilter): boolean {
  return Object.values(f).some((v) => v !== '')
}

/** A stored amount is PRESENT (vs. absent) — distinguishes a genuine 0 from
    null/undefined/empty so the total filter treats "no amount yet" as unknown
    (passes) rather than $0. */
function hasValue(v: unknown): boolean {
  return v !== null && v !== undefined && v !== ''
}

/** Sorted, de-duplicated, non-empty option list — feeds the Filter button's
    company/vendor (provider) dropdowns from the rows behind the timeline badges. */
export function distinct(values: (string | undefined)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b))
}

/** Text equality the way Dataverse `eq` does it — ignoring case. The client
    predicates below use this so a queue and an Archives lookup can never
    disagree about whether a row matches the company/vendor picked in the
    filter. */
function sameText(stored: string | null | undefined, wanted: string): boolean {
  return (stored ?? '').toLowerCase() === wanted.toLowerCase()
}

/** Translate the shared filter into the API's named query parameters, for the
    screens where the set is unbounded and paging must compose with the filter
    (Archives). The queue screens filter their loaded page in memory instead
    (`applyFilters`), and the two must agree — which is why the null-visibility
    rule is stated on both sides: a record with no total and no date stays
    VISIBLE under a range filter, because at Import Control those are exactly the
    rows still needing entry. The API keeps them unless `require_date` is set.

    Note what is no longer here: the client used to build an OData `$filter`
    string. That was coupling and an injection surface at once; every parameter
    below is named, typed and validated server-side. */
export function filterToParams(f: InvoiceFilter): Partial<InvoiceListParams> {
  const params: Partial<InvoiceListParams> = {}
  // Company and vendor are exact choices from the popover, sent as ids. A
  // company id is intersected with the caller's visible set server-side and can
  // never widen it.
  if (f.companyId) params.company_id = [f.companyId]
  if (f.vendorId) params.vendor_id = [f.vendorId]
  if (f.minTotal !== '') {
    const n = toNumber(f.minTotal)
    if (Number.isFinite(n)) params.total_min = n
  }
  if (f.maxTotal !== '') {
    const n = toNumber(f.maxTotal)
    if (Number.isFinite(n)) params.total_max = n
  }
  if (f.fromDate) params.date_from = f.fromDate
  if (f.toDate) params.date_to = f.toDate
  return params
}

export function applyFilters(invoices: Invoice[], f: InvoiceFilter): Invoice[] {
  return invoices.filter((inv) => {
    // Case-insensitive, like Dataverse `eq` — the Archives screen pushes the
    // same company/vendor choice into the query.
    // Match on the id when the popover supplied one; fall back to the name so a
    // row whose id was never captured still filters the way it reads.
    if (f.companyId) {
      if ((inv.company_id ?? '') !== f.companyId) return false
    } else if (f.company && !sameText(inv.company_name, f.company)) return false
    if (f.vendorId) {
      if ((inv.vendor_id ?? '') !== f.vendorId) return false
    } else if (f.vendor && !sameText(inv.vendor_name, f.vendor)) return false
    // Unknown values pass: a record with no total/date must stay visible (at
    // Import Control these are often the thing still to be entered).
    if (hasValue(inv.total)) {
      const total = toNumber(inv.total)
      if (f.minTotal !== '' && total < toNumber(f.minTotal)) return false
      if (f.maxTotal !== '' && total > toNumber(f.maxTotal)) return false
    }
    if (f.fromDate || f.toDate) {
      const d = (inv.invoice_date ?? '').slice(0, 10)
      if (d) {
        if (f.fromDate && d < f.fromDate) return false
        if (f.toDate && d > f.toDate) return false
      }
    }
    return true
  })
}
